import { useState } from 'react';
import { Filesystem, Directory } from '@capacitor/filesystem';
import {
  downloadFirmwareProduction,
  downloadFirmwareBeta,
} from 'services/Firmware';
import { getAuth } from '../util';
import { useFSM } from '../services/DongleController/DongleController';
import { StatesBLE } from 'services/BLE/BLE';

const isWeb =
  typeof window !== 'undefined' &&
  (!window.cordova || (window as any).Device?.platform === 'web');
let ZephyrDfu: any = null;
type ZephyrDfuUpdateFirmwareParams = {
  fileURL: string;
  deviceIdentifier: string;
};
if (!isWeb) {
  ZephyrDfu = require('zephyr-dfu').ZephyrDfu;
}


const useFirmwareUpdate = () => {
  const [version, setVersion] = useState<string>('Current Version not checked');
  const [logs, setLogs] = useState<string[]>([]);
  const fsm = useFSM();

  const log = (message: string) => {
    console.log(message);
    setLogs((_prev) => {
      const prev = [..._prev];
      const last = prev.pop();
      if (message?.includes('%') && last?.includes('%')) {
        const newLogs = [...prev, message];
        return newLogs;
      } else {
        return [...prev, last || '', message || 'undefined'];
      }
    });
  };

  const checkForUpdate = async (type: 'production' | 'beta' = 'production') => {
    try {
      const token = await getToken();
      if (!token) {
        log('You need to set a valid bearer token');
        return;
      }
      let fw_blob: Blob;
      let fileName = 'firmware.bin';
      if (type === 'production') {
        log('Checking for production firmware...');
        const result = await downloadFirmwareProduction(token);
        fw_blob = result.blob;
        fileName = result.filename;
      } else {
        log('Checking for beta firmware...');
        fw_blob = await downloadFirmwareBeta(token);
        fileName = 'beta_firmware.bin';
      }
      log(`Firmware received: ${fileName}`);
      if (!fw_blob || fw_blob.size === 0) {
        log('No firmware update available or received blob is empty.');
        return;
      }
      await saveFirmwareToFile(fw_blob, fileName);
    } catch (e) {
      log(`Error checking for update: ${e} `);
    }
  };

  const getToken = async (): Promise<string> => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (user && typeof user.getIdToken === 'function') {
        return await user.getIdToken();
      }
      return '';
    } catch (e) {
      log('Error getting auth token: ' + e);
      return '';
    }
  };

  const saveFirmwareToFile = async (blob: Blob, filename: string) => {
    const fileInfo = {
      directory: Directory.Data,
      path: filename,
    };
    const b64 = await blobToBase64(blob);

    const fileUrl = (await Filesystem.getUri(fileInfo)).uri;
    log(`fsm - updateFirmware - File URL: ${fileUrl} `);
    try {
      const res = await Filesystem.writeFile({
        ...fileInfo,
        recursive: true,
        data: b64,
      });

      log(`fsm - updateFirmware - File written successfully: ${res.uri} `);
      return await startUpdateProcess(res.uri);
    } catch (e) {
      log('fsm - updateFirmware - File does not exist');
    }
  };

  const startUpdateProcess = async (fileUrl: string) => {
    const lastDeviceId = fsm.deviceId.get() || '';
    log(`Starte Firmware-Update für Device-ID: ${lastDeviceId}`);
    fsm.setUserInputBle && fsm.setUserInputBle(StatesBLE.DISCONNECTED);

    await ZephyrDfu.updateFirmware(
      {
      fileURL: fileUrl,
      deviceIdentifier: lastDeviceId,
      } as ZephyrDfuUpdateFirmwareParams,
      (msg: any) => {
      log(`ZDFU ${JSON.stringify(msg)} `);
      }
    );
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const checkFirmwareVersion = async () => {
    try {
      log('Check firmware version...');
      const deviceId = fsm.deviceId.get() || '';
      const version = await ZephyrDfu.getVersion({
        deviceIdentifier: deviceId,
      });
      log(`Firmware version: ${JSON.stringify(version)} `);
      setVersion(`${JSON.stringify(version)} `);
    } catch (e) {
      log(e as string);
    } finally {
      log('Finished checkFirmwareVersion');
    }
  };

  return {
    checkForUpdate,
    checkFirmwareVersion,
    clearLogs,
    logs,
    version,
  } as const;
};

export const blobToBase64 = (blob: Blob) => {
  const reader = new FileReader();
  return new Promise<string>((resolve, reject) => {
    reader.onerror = reject;
    reader.onload = () => {
      resolve((reader.result as string).split('base64,')[1]);
    };
    reader.readAsDataURL(blob);
  });
};

export default useFirmwareUpdate;

export {};
